import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Pencil, Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ClientSearchInput } from './ClientSearchInput';

export function EditOrderDialog({ order, open, onOpenChange, onSuccess }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [clients, setClients] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [services, setServices] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    client_id: '',
    service_type_id: '',
    scheduled_date: '',
    scheduled_time_start: '',
    scheduled_time_end: '',
    address_street: '',
    price: '',
    discount: '',
    priority: 'normal',
    description: '',
    assigned_worker_ids: []
  });

  // Check for conflicts when worker, date, or time changes
  const checkConflicts = async (workerIds, date, timeStart, timeEnd) => {
    if (!workerIds?.length || !date || !timeStart) {
      setConflicts([]);
      return;
    }
    
    try {
      const response = await api.post('/orders/check-conflicts', {
        worker_ids: workerIds,
        scheduled_date: date,
        time_start: timeStart,
        time_end: timeEnd || null,
        exclude_order_id: order?.id, // Exclude current order
      });
      setConflicts(response.data.conflicts || []);
    } catch (err) {
      console.error('Failed to check conflicts:', err);
      setConflicts([]);
    }
  };

  // Trigger conflict check when relevant fields change
  useEffect(() => {
    if (!open) return;
    
    const timer = setTimeout(() => {
      checkConflicts(
        formData.assigned_worker_ids,
        formData.scheduled_date,
        formData.scheduled_time_start,
        formData.scheduled_time_end
      );
    }, 500); // Debounce
    
    return () => clearTimeout(timer);
  }, [formData.assigned_worker_ids, formData.scheduled_date, formData.scheduled_time_start, formData.scheduled_time_end, open]);

  // Load data when dialog opens
  useEffect(() => {
    if (open && order) {
      setFormData({
        title: order.title || '',
        client_id: order.client_id || '',
        service_type_id: order.service_type_id || '',
        scheduled_date: order.scheduled_date || '',
        scheduled_time_start: order.scheduled_time_start || '',
        scheduled_time_end: order.scheduled_time_end || '',
        address_street: order.address_street || order.address_string || '',
        price: order.price || '',
        discount: order.discount || '',
        priority: order.priority || 'normal',
        description: order.description || '',
        assigned_worker_ids: order.assigned_workers?.map(w => w.id) || []
      });
      
      // Fetch reference data
      Promise.all([
        api.get('/clients').catch(() => ({ data: [] })),
        api.get('/workers').catch(() => ({ data: [] })),
        api.get('/services').catch(() => ({ data: [] }))
      ]).then(([clientsRes, workersRes, servicesRes]) => {
        setClients(clientsRes.data || []);
        setWorkers(workersRes.data || []);
        setServices(servicesRes.data || []);
      });
    }
  }, [open, order, api]);

  const toggleWorker = (workerId) => {
    setFormData(prev => ({
      ...prev,
      assigned_worker_ids: prev.assigned_worker_ids.includes(workerId)
        ? prev.assigned_worker_ids.filter(id => id !== workerId)
        : [...prev.assigned_worker_ids, workerId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // VALIDATION: Block if there are conflicts
    if (conflicts.length > 0) {
      toast.error('Нельзя сохранить: у работника конфликт времени');
      return;
    }
    
    setLoading(true);
    
    try {
      // Update order
      await api.patch(`/orders/${order.id}`, {
        title: formData.title,
        client_id: formData.client_id || null,
        service_type_id: formData.service_type_id || null,
        scheduled_date: formData.scheduled_date || null,
        scheduled_time_start: formData.scheduled_time_start || null,
        scheduled_time_end: formData.scheduled_time_end || null,
        address_street: formData.address_street || null,
        price: formData.price ? parseFloat(formData.price) : 0,
        discount: formData.discount ? parseFloat(formData.discount) : 0,
        priority: formData.priority,
        description: formData.description
      });
      
      // Update worker assignments atomically via reassign endpoint
      const workerIdsChanged =
        JSON.stringify([...(formData.assigned_worker_ids || [])].sort()) !==
        JSON.stringify([...(order.assigned_workers?.map(w => w.id) || [])].sort());
      if (workerIdsChanged) {
        await api.post(`/orders/${order.id}/reassign`, { worker_ids: formData.assigned_worker_ids });
      }
      
      toast.success(t('orders.updated'));
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      console.error('Failed to update order:', err);
      toast.error(t('orders.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Редактировать заказ</DialogTitle>
          <DialogDescription>
            Изменение деталей заказа
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4">
            {/* Title */}
            <div className="space-y-2">
              <Label>Название заказа *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                placeholder="Название заказа"
                required
                data-testid="edit-order-title"
              />
            </div>

            {/* Client & Service */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Клиент</Label>
                <ClientSearchInput 
                  clients={clients}
                  value={formData.client_id}
                  onChange={(clientId) => setFormData(prev => ({...prev, client_id: clientId}))}
                  data-testid="edit-order-client"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Услуга</Label>
                <Select 
                  value={formData.service_type_id} 
                  onValueChange={(v) => setFormData(prev => ({...prev, service_type_id: v}))}
                >
                  <SelectTrigger data-testid="edit-order-service">
                    <SelectValue placeholder="Выберите услугу" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(service => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Дата</Label>
                <Input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData(prev => ({...prev, scheduled_date: e.target.value}))}
                  data-testid="edit-order-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Начало</Label>
                <Input
                  type="time"
                  value={formData.scheduled_time_start}
                  onChange={(e) => setFormData(prev => ({...prev, scheduled_time_start: e.target.value}))}
                  data-testid="edit-order-time-start"
                />
              </div>
              <div className="space-y-2">
                <Label>Окончание</Label>
                <Input
                  type="time"
                  value={formData.scheduled_time_end}
                  onChange={(e) => setFormData(prev => ({...prev, scheduled_time_end: e.target.value}))}
                  data-testid="edit-order-time-end"
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label>Адрес</Label>
              <Input
                value={formData.address_street}
                onChange={(e) => setFormData(prev => ({...prev, address_street: e.target.value}))}
                placeholder="Адрес выполнения работы"
                data-testid="edit-order-address"
              />
            </div>

            {/* Price & Discount */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Цена ($)</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({...prev, price: e.target.value}))}
                  placeholder="0"
                  min="0"
                  step="0.01"
                  data-testid="edit-order-price"
                />
              </div>
              <div className="space-y-2">
                <Label>Скидка ($)</Label>
                <Input
                  type="number"
                  value={formData.discount}
                  onChange={(e) => setFormData(prev => ({...prev, discount: e.target.value}))}
                  placeholder="0"
                  min="0"
                  step="0.01"
                  data-testid="edit-order-discount"
                />
              </div>
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <Label>Приоритет</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData(prev => ({...prev, priority: v}))}>
                <SelectTrigger data-testid="edit-order-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Низкий</SelectItem>
                  <SelectItem value="normal">Обычный</SelectItem>
                  <SelectItem value="high">Высокий</SelectItem>
                  <SelectItem value="urgent">Срочный</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Workers Assignment */}
            <div className="space-y-2">
              <Label>Назначить работников</Label>
              <div className="flex flex-wrap gap-2 p-3 rounded-lg border bg-muted/30">
                {workers.filter(w => w.is_active && (!w.role || w.role === 'worker')).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Нет доступных работников</p>
                ) : (
                  workers.filter(w => w.is_active && (!w.role || w.role === 'worker')).map(worker => (
                    <button
                      key={worker.id}
                      type="button"
                      onClick={() => toggleWorker(worker.id)}
                      className={cn(
                        "flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors",
                        formData.assigned_worker_ids.includes(worker.id)
                          ? "bg-accent text-accent-foreground border-accent"
                          : "bg-background hover:bg-muted"
                      )}
                    >
                      <div 
                        className="w-2.5 h-2.5 rounded-full" 
                        style={{ backgroundColor: worker.color }}
                      />
                      <span className="text-sm">{worker.first_name} {worker.last_name}</span>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Conflict Warning */}
            {conflicts.length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Конфликт времени!</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc list-inside mt-1">
                    {conflicts.map((c, i) => (
                      <li key={i}>
                        <strong>{c.worker_name}</strong>: уже назначен на "{c.conflicting_order_title}" в {c.conflicting_time_start}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Description */}
            <div className="space-y-2">
              <Label>Описание</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                placeholder="Дополнительная информация о заказе..."
                rows={3}
                data-testid="edit-order-description"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" disabled={loading} data-testid="edit-order-submit">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Pencil className="mr-2 h-4 w-4" />}
              Сохранить
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
